import { forwardRef } from "react";

import * as RPToggleGroup from "@radix-ui/react-toggle-group";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/
const ROOT_NAME = "ToggleGroup";

interface RootProps extends Omit<RPToggleGroup.ToggleGroupSingleProps, "type"> {
  rounded?: boolean;
}

const Root = forwardRef<HTMLDivElement, RootProps>(
  (
    { defaultValue = "", value: valueProp, onValueChange, disabled, rounded = false, className, children, ...props },
    forwardedRef,
  ) => {
    const [value, setValue] = useControllableState({
      prop: valueProp,
      onChange: onValueChange,
      defaultProp: defaultValue,
    });

    const handleValueChange = (value?: string) => {
      if (value) setValue(value);
    };

    return (
      <RPToggleGroup.Root
        {...props}
        type="single"
        ref={forwardedRef}
        asChild={false}
        className={cn(
          `font-medium relative isolate inline-grid h-[46px] min-w-max shrink-0 auto-cols-[1fr] grid-flow-col items-stretch rounded-[10px] bg-primary-gray text-center align-top text-primary-dark-blue [--segmented-control-transition-duration:100ms]`,
          rounded && "rounded-[50px]",
          className,
        )}
        value={value}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        {children}
        <div
          className={cn(
            `top-0 left-0 [.item[data-state=on]:nth-child(1)~&]:translate-x-0 pointer-events-none absolute -z-1 hidden h-full transition-transform duration-(--segmented-control-transition-duration) ease-[cubic-bezier(0.445,0.05,0.55,0.95)] nth-10:w-[calc(100%/9)] nth-11:w-[calc(100%/10)] nth-2:w-full nth-3:w-[calc(100%/2)] nth-4:w-[calc(100%/3)] nth-5:w-[calc(100%/4)] nth-6:w-[calc(100%/5)] nth-7:w-[calc(100%/6)] nth-8:w-[calc(100%/7)] nth-9:w-[calc(100%/8)] [&::before]:absolute [&::before]:inset-px [&::before]:rounded-[max(0.5px,calc(10px-1px))] [&::before]:bg-primary-dark-blue [&::before]:content-[''] data-[disabled=true]:[&::before]:bg-primary-dark-gray [.item[data-state=on]:nth-child(10)~&]:translate-x-[calc(100%*9)] [.item[data-state=on]:nth-child(2)~&]:translate-x-full [.item[data-state=on]:nth-child(3)~&]:translate-x-[calc(100%*2)] [.item[data-state=on]:nth-child(4)~&]:translate-x-[calc(100%*3)] [.item[data-state=on]:nth-child(5)~&]:translate-x-[calc(100%*4)] [.item[data-state=on]:nth-child(6)~&]:translate-x-[calc(100%*5)] [.item[data-state=on]:nth-child(7)~&]:translate-x-[calc(100%*6)] [.item[data-state=on]:nth-child(8)~&]:translate-x-[calc(100%*7)] [.item[data-state=on]:nth-child(9)~&]:translate-x-[calc(100%*8)] [.item[data-state=on]~&]:block`,
          )}
          data-disabled={disabled}
        />
      </RPToggleGroup.Root>
    );
  },
);
Root.displayName = ROOT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Item
 * -----------------------------------------------------------------------------------------------*/
const ITEM_NAME = "ToggleGroupItem";

type ItemProps = Omit<RPToggleGroup.ToggleGroupItemProps, "type">;

const Item = forwardRef<HTMLButtonElement, ItemProps>(({ className, children, ...props }, forwardedRef) => {
  return (
    <RPToggleGroup.Item
      {...props}
      ref={forwardedRef}
      asChild={false}
      className={cn(
        `item group/item flex cursor-pointer items-stretch select-none first:rounded-tl-[inherit] first:rounded-bl-[inherit] disabled:cursor-auto nth-last-2:rounded-tr-[inherit] nth-last-2:rounded-br-[inherit]`,
        className,
      )}
    >
      <span
        className={cn(
          "py-0 box-border flex grow items-center justify-center rounded-[inherit] px-[20px] [&_svg]:shrink-0",
        )}
      >
        <span
          className={cn(
            `ease-in group-data-[state=on]/item:ease-out flex items-center gap-[4px] text-primary-white opacity-0 transition-opacity duration-[calc(0.8*var(--segmented-control-transition-duration))] group-data-[state=on]/item:opacity-100`,
          )}
        >
          {children}
        </span>
        <span
          className={cn(
            `ease-out group-data-[state=on]/item:ease-in absolute flex items-center gap-[4px] opacity-100 transition-opacity duration-[calc(0.8*var(--segmented-control-transition-duration))] group-data-[state=on]/item:opacity-0`,
          )}
        >
          {children}
        </span>
      </span>
    </RPToggleGroup.Item>
  );
});

Item.displayName = ITEM_NAME;

export { Item, Root };
export type { ItemProps, RootProps };
